import React, { useState } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify } from '../../core/helpers';
import { sb } from '../../core/db';
import { KRAYA } from '../../core/constants';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';


function CreateInvoiceModal({ data, onClose }) {
  const { clients, invoices, dbCreateInvoice, bundles, org, showToast, runSubmitting } = useApp();
  const [clientId, setClientId] = useState(data?.clientId || "");
  const [bundleId, setBundleId] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [instalments, setInstalments] = useState([]); // #36 { label, amount, dueDate }
  const [step, setStep] = useState(1);

  const selectedBundle = bundles.find(b => b.id === bundleId);
  const selectedClient = clients.find(c => c.id === clientId);

  const loadBundle = (bId) => {
    setBundleId(bId);
    const b = bundles.find(x => x.id === bId);
    if (b) {
      setLineItems(b.lineItems.map(li => ({
        ...li,
        id: uuid(),
        originalId: li.id,
        qty: 1,
        unitPrice: li.price || 0,
        customPrice: !li.price,
      })));
    }
  };

  const setItem = (id, k, v) => setLineItems(ls => ls.map(l => l.id === id ? { ...l, [k]: v } : l));

  const calcAmount = (li) => {
    const base = li.unitPrice * li.qty;
    return li.gst ? base * (1 + org.gstRate / 100) : base;
  };

  const total = lineItems.reduce((s, li) => s + calcAmount(li), 0);
  const subtotal = lineItems.reduce((s, li) => s + li.unitPrice * li.qty, 0);
  const gstAmt = total - subtotal;

  // Tasks are auto-created server-side from each line item's task templates
  // (server/modules/invoices/task-generator.js) when the invoice is created.

  const createInvoice = async () => {
    if (!clientId) { showToast("Select a client", "error"); return; }
    if (lineItems.length === 0) { showToast("Add at least one line item", "error"); return; }

    const validIns = instalments.filter(x => x.label && Number(x.amount) > 0);
    if (validIns.length && validIns.reduce((s, x) => s + Number(x.amount), 0) > total + 0.01) {
      showToast("Instalments exceed the invoice total", "error"); return;
    }

    await runSubmitting('Creating invoice…', async () => {
    const invoiceNo = (() => {
      const yr = new Date().getFullYear();
      const yrInvoices = invoices.filter(i => (i.invoiceNo || "").includes("/" + yr + "/"));
      const maxNum = yrInvoices.reduce((mx, i) => {
        const n = parseInt((i.invoiceNo || "").split("/").pop()) || 0;
        return n > mx ? n : mx;
      }, 0);
      return "FB/" + yr + "/" + String(maxNum + 1).padStart(3, "0");
    })();
    const newInvoice = {
      id: uuid(), invoiceNo,
      clientId, clientName: selectedClient?.name || "",
      date: today(), dueDate: dueDate || null,
      status: "unpaid", total, paid: 0, pending: total,
      lineItems,
      isRecurring: !!selectedBundle?.isRecurring,
      recurringFreq: selectedBundle?.recurringFreq || null,
    };

    if (selectedClient?.phone) {
      KRAYA.invoiceCreated(selectedClient.phone, selectedClient.contactName || selectedClient.name, INR(total), invoiceNo);
    }

    const sched = validIns.length
      ? { rows: validIns.map((x, i) => ({ label: x.label, amount: Number(x.amount), dueDate: x.dueDate || null, seq: i + 1 })) }
      : (selectedBundle && selectedBundle.installments > 1)
        ? { installments: selectedBundle.installments, freq: selectedBundle.recurringFreq }
        : null;
    try {
      await dbCreateInvoice(newInvoice, lineItems.map(li => ({ ...li, amount: calcAmount(li) })), sched);
      onClose();
    } catch (e) {
      showToast(e?.message || "Could not create invoice", "error");
    }
    }, 'invoice');
  };

  return (
    <div className="modal-box" style={{ maxWidth: 700 }}>
      <div className="modal-head">
        <div className="modal-title">Create Invoice</div>
        <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>
      <div className="modal-body">
        {/* Step 1 — Select client + bundle */}
        {step === 1 && (
          <div className="form-grid">
            <div className="f-group">
              <label className="f-label">Client <span className="f-req">*</span></label>
              <select className="f-select" value={clientId} onChange={e => setClientId(e.target.value)}>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="f-group">
              <label className="f-label">Start from Bundle (optional)</label>
              <select className="f-select" value={bundleId} onChange={e => loadBundle(e.target.value)}>
                <option value="">Select bundle to auto-fill…</option>
                {bundles.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <div className="f-hint">Selecting a bundle auto-fills line items and creates tasks</div>
            </div>
            <div className="f-group">
              <label className="f-label">Due Date</label>
              <input type="date" className="f-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
              <div className="f-hint">Client will be notified via WhatsApp when invoice is created</div>
            </div>
            {lineItems.length === 0 && (
              <button className="btn btn-ghost" onClick={() => setLineItems([{ id: uuid(), name: "", type: "service", qty: 1, unitPrice: 0, gst: true, unit: "fixed", tasks: [], customPrice: true }])}>
                + Add Line Item Manually
              </button>
            )}
          </div>
        )}

        {/* Line items */}
        {lineItems.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, marginTop: step === 1 ? 20 : 0 }}>Line Items</div>
            <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
              <table>
                <thead>
                  <tr>
                    <th>Item</th><th>SAC</th><th>Type</th><th>Qty</th><th>Unit Price (₹)</th><th>GST</th><th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map(li => (
                    <tr key={li.id}>
                      <td style={{ minWidth: 180 }}>
                        <input className="f-input" value={li.name} onChange={e => setItem(li.id, "name", e.target.value)} style={{ fontSize: 12, padding: "5px 9px" }} />
                        {li.note && <div style={{ fontSize: 10, color: "var(--orange)", marginTop: 2 }}><InlineIcon name="warning" size={11} gap={4}>{li.note}</InlineIcon></div>}
                      </td>
                      <td>
                        <input className="f-input" value={li.sac || org.sac || ""} onChange={e => setItem(li.id, "sac", e.target.value)} style={{ width: 80, fontSize: 12, padding: "5px 9px" }} />
                        {/* HARDCODED SAC "998211" removed — use org.sac */}
                      </td>
                      <td><span className="tag">{li.type}</span></td>
                      <td>
                        <input type="number" className="f-input" value={li.qty} min={1} onChange={e => setItem(li.id, "qty", Math.max(1, Number(e.target.value)))} style={{ width: 60, fontSize: 12, padding: "5px 9px" }} />
                        {li.type === "dsc" && <div style={{ fontSize: 10, color: "var(--blue)" }}>{li.qty} task{li.qty > 1 ? "s" : ""}</div>}
                      </td>
                      <td>
                        <input type="number" className="f-input" value={li.unitPrice} onChange={e => setItem(li.id, "unitPrice", Number(e.target.value))} style={{ width: 90, fontSize: 12, padding: "5px 9px" }} />
                      </td>
                      <td style={{ fontSize: 12 }}>{li.gst ? `+${org.gstRate}%` : "Nil"}</td>
                      <td style={{ fontWeight: 700, fontSize: 13 }}>{INR(calcAmount(li))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ background: "#FAFAF8", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 18px" }}>
              {[["Subtotal", INR(subtotal)], [`GST (${org.gstRate}%)`, INR(gstAmt)], ["Total", INR(total)]].map(([l, v], i) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderTop: i === 2 ? "2px solid var(--border)" : "none", marginTop: i === 2 ? 8 : 0 }}>
                  <span style={{ fontSize: 13, fontWeight: i === 2 ? 700 : 400, color: i === 2 ? "var(--ink)" : "var(--muted)" }}>{l}</span>
                  <span style={{ fontSize: i === 2 ? 16 : 13, fontWeight: 700, color: i === 2 ? "var(--ink)" : "var(--muted)" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* #36 Instalments (optional) — defined at invoice creation */}
            <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 16px", marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>Split into instalments (optional)</div>
                <button className="btn btn-sm" onClick={() => setInstalments(is => [...is, { label: `Instalment ${is.length + 1}`, amount: "", dueDate: "" }])}>+ Add instalment</button>
              </div>
              {instalments.length === 0 && <div style={{ fontSize: 11, color: "var(--muted)" }}>Leave empty to bill the full amount at once.</div>}
              {instalments.map((ins, idx) => (
                <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <input className="f-input" style={{ flex: 1 }} placeholder="Label (e.g. Advance)" value={ins.label} onChange={e => setInstalments(is => is.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} />
                  <input className="f-input" style={{ width: 110 }} type="number" placeholder="Amount ₹" value={ins.amount} onChange={e => setInstalments(is => is.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} />
                  <input className="f-input" style={{ width: 150 }} type="date" value={ins.dueDate} onChange={e => setInstalments(is => is.map((x, i) => i === idx ? { ...x, dueDate: e.target.value } : x))} />
                  <span style={{ cursor: "pointer", color: "#B91C1C", fontWeight: 700 }} title="Remove" onClick={() => setInstalments(is => is.filter((_, i) => i !== idx))}>×</span>
                </div>
              ))}
              {instalments.length > 0 && (() => {
                const sum = instalments.reduce((s, x) => s + (Number(x.amount) || 0), 0);
                const diff = total - sum;
                return <div style={{ fontSize: 11, marginTop: 4, fontWeight: 600, color: Math.abs(diff) < 0.01 ? "var(--green)" : "var(--red)" }}>
                  Instalments ₹{sum.toLocaleString("en-IN")} · {Math.abs(diff) < 0.01 ? "matches invoice total" : (diff > 0 ? `₹${diff.toLocaleString("en-IN")} still unallocated` : `exceeds total by ₹${(-diff).toLocaleString("en-IN")}`)}
                </div>;
              })()}
            </div>

            {lineItems.filter(li => li.type !== "govt").some(li => {
              const b = bundles.find(x => x.id === bundleId);
              const orig = b?.lineItems.find(x => x.id === li.originalId);
              return orig?.tasks?.length > 0;
            }) && (
                <div className="info-box" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", marginTop: 16 }}>
                  <Icon name="sparkles" size={16} color="var(--green)" />
                  <div style={{ fontSize: 12, color: "var(--green)" }}>
                    <strong>Tasks will be auto-created</strong> from this invoice based on line items and quantities. DSC tasks will create 1 task per director.
                  </div>
                </div>
              )}
          </>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={createInvoice} disabled={!clientId || lineItems.length === 0}>
          Create Invoice & Tasks →
        </button>
      </div>
    </div>
  );
}

// ─── View Invoice Modal ───────────────────────────────────────────────



export { CreateInvoiceModal };